// src/admin/AboutAdmin.jsx
import React from "react";
import { v4 as uuidv4 } from "uuid";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { db, storage } from "../../firebase";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

/* ===================== QUILL ===================== */
const quillModules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ size: ["small", false, "large", "huge"] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ script: "sub" }, { script: "super" }],
      ["blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }, { direction: "rtl" }],
      ["link", "image", "video"],
      ["clean"],
    ],
  },
  clipboard: { matchVisual: true },
};

const quillFormats = [
  "header",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "script",
  "blockquote",
  "code-block",
  "list",
  "indent",
  "align",
  "direction",
  "link",
  "image",
  "video",
];

export default function AboutAdmin() {
  /* ========= СЕКЦІЇ ========= */
  const [sections, setSections] = React.useState([]);
  const [editing, setEditing] = React.useState(null);

  React.useEffect(() => {
    const secQ = query(
      collection(db, "pages", "about", "sections"),
      orderBy("order", "asc")
    );
    const unsubSec = onSnapshot(secQ, (snap) => {
      setSections(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsubSec();
  }, []);

  const newSection = async () => {
    const nextOrder = (sections[sections.length - 1]?.order || 0) + 1;
    const docRef = await addDoc(collection(db, "pages", "about", "sections"), {
      title: "Нова секція",
      anchor: `section-${uuidv4().slice(0, 8)}`,
      style: "default",
      inNav: true,
      order: nextOrder,
      contentHtml: "<p>Новий контент…</p>",
      updatedAt: serverTimestamp(),
    });
    setEditing({
      id: docRef.id,
      title: "Нова секція",
      anchor: "",
      style: "default",
      inNav: true,
      order: nextOrder,
      contentHtml: "<p>Новий контент…</p>",
    });
  };

  const selectSection = (s) => setEditing(s);

  const saveSection = async () => {
    if (!editing) return;
    await updateDoc(doc(db, "pages", "about", "sections", editing.id), {
      title: editing.title,
      anchor: editing.anchor || editing.id,
      style: editing.style || "default",
      inNav: !!editing.inNav,
      order: Number(editing.order) || 0,
      contentHtml: editing.contentHtml || "",
      updatedAt: serverTimestamp(),
    });
  };

  const deleteSection = async (id) => {
    await deleteDoc(doc(db, "pages", "about", "sections", id));
    if (editing?.id === id) setEditing(null);
  };

  const moveSection = async (id, dir) => {
    const idx = sections.findIndex((s) => s.id === id);
    const targetIdx = idx + dir;
    if (idx < 0 || targetIdx < 0 || targetIdx >= sections.length) return;
    const batch = writeBatch(db);
    const a = sections[idx],
      b = sections[targetIdx];
    batch.update(doc(db, "pages", "about", "sections", a.id), {
      order: b.order,
    });
    batch.update(doc(db, "pages", "about", "sections", b.id), {
      order: a.order,
    });
    await batch.commit();
  };

  /* ========= КОМАНДА ========= */
  const [team, setTeam] = React.useState([]);
  const [member, setMember] = React.useState({});
  const [uploadPct, setUploadPct] = React.useState(0);
  const [uploadErr, setUploadErr] = React.useState("");

  React.useEffect(() => {
    const teamQ = query(
      collection(db, "pages", "about", "team"),
      orderBy("order", "asc")
    );
    const unsubTeam = onSnapshot(teamQ, (snap) => {
      setTeam(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsubTeam();
  }, []);

  const editMember = (m) =>
    setMember({
      ...m,
      _oldAvatarPath: m?.avatarPath || "", // потрібно для видалення старого файлу після save
    });

  const saveMember = async () => {
    const payload = {
      name: member.name || "",
      role: member.role || "",
      phone: member.phone || "",
      email: member.email || "",
      viberUrl: member.viberUrl || "",
      telegramUrl: member.telegramUrl || "",
      avatarUrl: member.avatarUrl || "",
      avatarPath: member.avatarPath || "",
      order: Number(member.order) || 0,
      updatedAt: serverTimestamp(),
    };

    if (member.id) {
      await updateDoc(doc(db, "pages", "about", "team", member.id), payload);
    } else {
      const refDoc = await addDoc(
        collection(db, "pages", "about", "team"),
        payload
      );
      payload.id = refDoc.id;
    }

    // Якщо змінено шлях — видаляємо старий файл у Storage
    if (member._oldAvatarPath && member._oldAvatarPath !== member.avatarPath) {
      try {
        await deleteObject(ref(storage, member._oldAvatarPath));
      } catch {
        // Файл могли вже прибрати — ігноруємо
      }
    }

    setMember({});
    setUploadPct(0);
    setUploadErr("");
  };

  const deleteMember = async (id) => {
    // Видаляємо фото з бакета, якщо є шлях
    const m = team.find((t) => t.id === id);
    if (m?.avatarPath) {
      try {
        await deleteObject(ref(storage, m.avatarPath));
      } catch {
        /* ignore */
      }
    }
    await deleteDoc(doc(db, "pages", "about", "team", id));
    if (member?.id === id) setMember({});
  };

  const moveMember = async (id, dir) => {
    const idx = team.findIndex((t) => t.id === id);
    const targetIdx = idx + dir;
    if (idx < 0 || targetIdx < 0 || targetIdx >= team.length) return;
    const batch = writeBatch(db);
    const a = team[idx],
      b = team[targetIdx];
    batch.update(doc(db, "pages", "about", "team", a.id), { order: b.order });
    batch.update(doc(db, "pages", "about", "team", b.id), { order: a.order });
    await batch.commit();
  };

  // Завантаження аватару з прогресом, збереженням URL+path
  const uploadAvatar = async (file) => {
    setUploadErr("");
    setUploadPct(0);
    if (!file) return;

    const safeName = file.name.replace(/\s+/g, "_");
    const path = `about/team/${uuidv4()}_${safeName}`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type || "image/jpeg",
      cacheControl: "public, max-age=31536000, immutable",
    });

    task.on(
      "state_changed",
      (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        setUploadPct(pct);
      },
      (err) => {
        setUploadErr(err?.message || "Помилка завантаження");
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        setMember((m) => ({
          ...m,
          avatarUrl: url,
          avatarPath: path,
        }));
      }
    );
  };

  // Очистити фото у формі (при збереженні — старе буде видалено)
  const clearAvatar = () => {
    setMember((m) => ({ ...m, avatarUrl: "", avatarPath: "" }));
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 24,
        padding: 16,
      }}
    >
      {/* ==== СЕКЦІЇ ==== */}
      <section>
        <h2>Секції сторінки</h2>
        <button onClick={newSection}>+ Додати секцію</button>

        <ul style={{ marginTop: 12, padding: 0, listStyle: "none" }}>
          {sections.map((s) => (
            <li
              key={s.id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 12,
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <strong>{s.title}</strong>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => moveSection(s.id, -1)}>↑</button>
                  <button onClick={() => moveSection(s.id, +1)}>↓</button>
                  <button onClick={() => selectSection(s)}>Редагувати</button>
                  <button
                    onClick={() => deleteSection(s.id)}
                    style={{ color: "crimson" }}
                  >
                    Видалити
                  </button>
                </div>
              </div>
              <div style={{ opacity: 0.7, fontSize: 12 }}>
                anchor: #{s.anchor} | стиль: {s.style} | в наві:{" "}
                {String(s.inNav)} | order: {s.order}
              </div>
            </li>
          ))}
        </ul>

        {editing && (
          <div
            style={{
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <h3>Редагування: {editing.title}</h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <label>
                Назва
                <input
                  value={editing.title}
                  onChange={(e) =>
                    setEditing({ ...editing, title: e.target.value })
                  }
                />
              </label>
              <label>
                Anchor (без #)
                <input
                  value={editing.anchor || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, anchor: e.target.value })
                  }
                />
              </label>
              <label>
                Стиль заголовка
                <select
                  value={editing.style || "default"}
                  onChange={(e) =>
                    setEditing({ ...editing, style: e.target.value })
                  }
                >
                  <option value="default">default</option>
                  <option value="manifest">manifest</option>
                </select>
              </label>
              <label>
                Порядок
                <input
                  type="number"
                  value={editing.order || 0}
                  onChange={(e) =>
                    setEditing({ ...editing, order: e.target.value })
                  }
                />
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={!!editing.inNav}
                  onChange={(e) =>
                    setEditing({ ...editing, inNav: e.target.checked })
                  }
                />
                Показувати у наві
              </label>
            </div>

            <div style={{ marginTop: 12 }}>
              <ReactQuill
                theme="snow"
                value={editing.contentHtml || ""}
                onChange={(v) => setEditing({ ...editing, contentHtml: v })}
                modules={quillModules}
                formats={quillFormats}
                placeholder="HTML секції…"
              />
            </div>

            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <button onClick={saveSection}>Зберегти секцію</button>
              <button onClick={() => setEditing(null)}>Закрити</button>
            </div>
          </div>
        )}
      </section>

      {/* ==== КОМАНДА ==== */}
      <section>
        <h2>Колектив (візитівки)</h2>
        <button
          onClick={() =>
            editMember({ order: (team[team.length - 1]?.order || 0) + 1 })
          }
        >
          + Додати працівника
        </button>

        <ul style={{ marginTop: 12, padding: 0, listStyle: "none" }}>
          {team.map((t) => (
            <li
              key={t.id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 12,
                marginBottom: 8,
                display: "grid",
                gridTemplateColumns: "80px 1fr auto",
                gap: 12,
              }}
            >
              <img
                src={t.avatarUrl}
                alt={t.name}
                style={{
                  width: 80,
                  height: 80,
                  objectFit: "cover",
                  borderRadius: 8,
                  background: "#f3f4f6",
                }}
              />
              <div>
                <strong>{t.name}</strong> — {t.role}
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  {t.phone} {t.email && <> | {t.email}</>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button onClick={() => moveMember(t.id, -1)}>↑</button>
                <button onClick={() => moveMember(t.id, +1)}>↓</button>
                <button onClick={() => editMember(t)}>Редагувати</button>
                <button
                  onClick={() => deleteMember(t.id)}
                  style={{ color: "crimson" }}
                >
                  Видалити
                </button>
              </div>
            </li>
          ))}
        </ul>

        {Object.keys(member).length > 0 && (
          <div
            style={{
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <h3>{member.id ? "Редагувати працівника" : "Новий працівник"}</h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <label>
                ПІБ
                <input
                  value={member.name || ""}
                  onChange={(e) =>
                    setMember({ ...member, name: e.target.value })
                  }
                />
              </label>
              <label>
                Посада
                <input
                  value={member.role || ""}
                  onChange={(e) =>
                    setMember({ ...member, role: e.target.value })
                  }
                />
              </label>
              <label>
                Телефон
                <input
                  value={member.phone || ""}
                  onChange={(e) =>
                    setMember({ ...member, phone: e.target.value })
                  }
                />
              </label>
              <label>
                Email
                <input
                  value={member.email || ""}
                  onChange={(e) =>
                    setMember({ ...member, email: e.target.value })
                  }
                />
              </label>
              <label>
                Viber URL
                <input
                  value={member.viberUrl || ""}
                  onChange={(e) =>
                    setMember({ ...member, viberUrl: e.target.value })
                  }
                  placeholder="viber://chat?number=%2B380..."
                />
              </label>
              <label>
                Telegram URL
                <input
                  value={member.telegramUrl || ""}
                  onChange={(e) =>
                    setMember({ ...member, telegramUrl: e.target.value })
                  }
                  placeholder="https://t.me/... або https://teleg.one/..."
                />
              </label>
              <label>
                Порядок
                <input
                  type="number"
                  value={member.order || 0}
                  onChange={(e) =>
                    setMember({ ...member, order: e.target.value })
                  }
                />
              </label>

              <label>
                Фото (jpg/png/webp)
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => uploadAvatar(e.target.files?.[0])}
                />
                {uploadPct > 0 && uploadPct < 100 && (
                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                    Завантаження: {uploadPct}%
                  </div>
                )}
                {uploadErr && (
                  <div style={{ color: "crimson", fontSize: 12 }}>
                    {uploadErr}
                  </div>
                )}
              </label>

              {member.avatarUrl && (
                <div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    Попередній перегляд:
                  </div>
                  <img
                    src={member.avatarUrl}
                    alt=""
                    style={{
                      width: 100,
                      height: 100,
                      objectFit: "cover",
                      borderRadius: 8,
                    }}
                  />
                  <div style={{ marginTop: 8 }}>
                    <button type="button" onClick={clearAvatar}>
                      Очистити фото
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <button onClick={saveMember}>Зберегти</button>
              <button
                onClick={() => {
                  setMember({});
                  setUploadPct(0);
                  setUploadErr("");
                }}
              >
                Скасувати
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
